import { SkillEnum, TestTypeEnum } from '@doltech/utils/lib/constants';
import * as React from 'react';
import { useBeforeUnload, useInterval } from 'react-use';

import { updateUserTestProgress } from './api/do-test';
import { TestSubmissionStoreInstance } from './model/test-submissions.store';

const ONE_MINUTE = 60000;

const canAutoSaveTest = (store: TestSubmissionStoreInstance) => {
  return store.isStartedTest;
};
const canManualSaveTest = (store: TestSubmissionStoreInstance) =>
  store.testType === TestTypeEnum.PRACTICE_TEST || store.testType === TestTypeEnum.FULL_TEST;

export const useFullTestAutoSave = (store: TestSubmissionStoreInstance, trialUserId: string) => {
  const { autoSave, manualSaveRef } = useSaveFullTestProgress(store, trialUserId);
  const autoSaveEnabled = canAutoSaveTest(store);
  const { currentSectionIndex, testType, skill, isReviewing } = store;

  useBeforeUnload(
    autoSaveEnabled,
    `Full test ${store.name} is in-progress, are you sure that you want to leave?`
  );

  // on section changed for listening full test, and not in review mode
  // save progress
  React.useEffect(() => {
    if (
      TestTypeEnum.FULL_TEST === testType &&
      SkillEnum.LISTENING === skill &&
      !isReviewing &&
      store.isStartedTest
    ) {
      manualSaveRef.current();
    }
  }, [manualSaveRef, currentSectionIndex, skill, testType, isReviewing, store.isStartedTest]);

  useInterval(() => autoSave(), autoSaveEnabled ? ONE_MINUTE : null);
};

export const useSaveFullTestProgress = (
  store: TestSubmissionStoreInstance,
  trialUserId: string
) => {
  const manualSaveRef = React.useRef<any>(() => Promise.reject());

  const handleAutoSave = async () => {
    const sectionIndex =
      store.skill === SkillEnum.LISTENING ? store.currentAudioIndex : store.currentSectionIndex;
    const payload = store.prepareDataForSubmit();
    await updateUserTestProgress({
      userTestId: store.id,
      payload,
      currentSectionIndex: store.isReviewing ? -1 : sectionIndex,
      trialUserId,
    });
  };

  return {
    autoSave: canAutoSaveTest(store) ? handleAutoSave : () => Promise.reject(),
    manualSave: canManualSaveTest(store) ? handleAutoSave : () => Promise.reject(),
    manualSaveRef,
  };
};
